import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import {
  discardWorkflowPendingChanges,
  publishWorkflowPendingChanges,
  saveWorkflowPendingChanges,
} from '@/lib/api/generated/api';
import type {
  ImportViolation,
  WorkflowEditView,
  WorkflowPendingChangesSave,
} from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';
import { useSession } from '@/lib/auth/session';

import {
  PendingChangesOutdatedError,
  WorkflowGoneError,
  WorkflowRejectedError,
  WorkflowStepsInUseError,
  WorkflowVersionConflictError,
} from '../errors';
import { clearBuilderBackup } from '../builder/builderBackupStore';
import { workflowKeys } from '../queryKeys';

/**
 * The three writes on a published workflow's pending changes (spec 144, over
 * service spec 127): save them, put them live, throw them away.
 *
 * Each answers with what the builder should show next, and each leaves the
 * editing read holding exactly that, so the builder and its cache cannot
 * disagree about which revision the next write must quote. The workspace is
 * re-read as well, because the sidebar's Draft marker comes from it.
 */

/**
 * A 409 body, whichever door refused.
 *
 * The generated client types these as the shared error envelope or as a
 * rejection, and neither type has both `error` and `violations`; a steps-in-use
 * refusal carries both. Narrowed here once, as `useReplaceWorkflow` does.
 */
type Conflict409 = {
  error?: string;
  message?: string | null;
  violations?: ImportViolation[];
};

function refusalOf409(data: Conflict409): Error {
  if (data.error === 'WORKFLOW_STEPS_IN_USE' && data.violations) {
    return new WorkflowStepsInUseError(409, { violations: data.violations });
  }
  if (data.error === 'WORKFLOW_PENDING_CHANGES_OUTDATED') {
    return new PendingChangesOutdatedError();
  }
  // A stale revision or version: the copy on screen is not what the service
  // holds, and reloading is the way forward.
  if (data.error === 'WORKFLOW_VERSION_CONFLICT') {
    return new WorkflowVersionConflictError(data.message ?? undefined);
  }
  // Anything else is not known to be staleness, and offering a reload for it
  // would send the author round in a circle. The service's own words say more
  // than a guess would.
  return refusedBecause(data);
}

/**
 * A 404 from a pending-changes door: the workflow is gone, or its pending
 * changes are — discarded or published by someone else since this copy was
 * read, which is a stale copy rather than a missing workflow.
 */
function refusalOf404(data: Conflict409): Error {
  if (data.error === 'WORKFLOW_NO_PENDING_CHANGES') {
    return new WorkflowVersionConflictError(data.message ?? undefined);
  }
  if (data.error === 'WORKFLOW_NOT_FOUND') return new WorkflowGoneError();
  // Something else is missing — not the workflow, so not "it no longer
  // exists".
  return refusedBecause(data);
}

/** A refusal this client has no special handling for, in the service's words. */
function refusedBecause(data: Conflict409): Error {
  return new Error(
    data.message ??
      `The service refused this: ${data.error ?? 'no reason given'}`,
  );
}

async function settle(
  queryClient: QueryClient,
  workspaceId: string,
  edit: WorkflowEditView,
  owner: string | null,
): Promise<void> {
  // Saved, published or discarded: the browser's backup of the edits is done
  // with (FR-008). Here rather than in the builder, which may already have
  // gone — Save draft and leave navigates the moment this resolves.
  clearBuilderBackup(owner, edit.workflow.id);
  queryClient.setQueryData(
    workflowKeys.editing(workspaceId, edit.workflow.id),
    edit,
  );
  // Awaited, as `useReplaceWorkflow` does, so the header, the sidebar and the
  // list move together.
  await invalidateWorkspace(queryClient, workspaceId);
}

/** Save the draft as the published workflow's pending changes (FR-003). */
export function useSavePendingChanges(workspaceId: string) {
  const queryClient = useQueryClient();
  const owner = useSession().user?.id ?? null;

  return useMutation({
    mutationFn: async ({
      workflowId,
      body,
    }: {
      workflowId: string;
      body: WorkflowPendingChangesSave;
    }): Promise<WorkflowEditView> => {
      const response = await saveWorkflowPendingChanges(
        workspaceId,
        workflowId,
        body,
      );
      if (response.status === 200) return response.data;
      if (response.status === 409) throw refusalOf409(response.data);
      if (response.status === 422) {
        throw new WorkflowRejectedError(422, response.data);
      }
      if (response.status === 404) throw refusalOf404(response.data);
      throw new Error('The draft could not be saved.');
    },
    onSuccess: (edit) => settle(queryClient, workspaceId, edit, owner),
  });
}

/** Put the pending changes live (FR-004). */
export function usePublishPendingChanges(workspaceId: string) {
  const queryClient = useQueryClient();
  const owner = useSession().user?.id ?? null;

  return useMutation({
    mutationFn: async ({
      workflowId,
      revision,
    }: {
      workflowId: string;
      revision: string;
    }): Promise<WorkflowEditView> => {
      const response = await publishWorkflowPendingChanges(
        workspaceId,
        workflowId,
        { expected_revision: revision },
      );
      // Published, the workflow has no pending changes left: the builder shows
      // the live definition, which is now what was the draft.
      if (response.status === 200) {
        return { workflow: response.data, pending_changes: null };
      }
      if (response.status === 409) throw refusalOf409(response.data);
      if (response.status === 422) {
        throw new WorkflowRejectedError(422, response.data);
      }
      if (response.status === 404) throw refusalOf404(response.data);
      throw new Error('The changes could not be published.');
    },
    onSuccess: (edit) => settle(queryClient, workspaceId, edit, owner),
  });
}

/**
 * Throw the pending changes away (FR-006), returning the published workflow
 * as the builder should now show it.
 *
 * The service answers with no body, so the published workflow is the one the
 * editing read already holds: a discard changes nothing about it (service
 * spec 127 FR-013).
 */
export function useDiscardPendingChanges(workspaceId: string) {
  const queryClient = useQueryClient();
  const owner = useSession().user?.id ?? null;

  return useMutation({
    mutationFn: async ({
      workflowId,
      revision,
    }: {
      workflowId: string;
      revision: string;
    }): Promise<WorkflowEditView> => {
      const response = await discardWorkflowPendingChanges(
        workspaceId,
        workflowId,
        { expected_revision: revision },
      );
      if (response.status === 409) throw refusalOf409(response.data);
      if (response.status === 404) throw refusalOf404(response.data);
      if (response.status !== 204) {
        throw new Error('The draft could not be discarded.');
      }

      const loaded = queryClient.getQueryData<WorkflowEditView>(
        workflowKeys.editing(workspaceId, workflowId),
      );
      if (!loaded) throw new Error('The draft could not be discarded.');
      return { workflow: loaded.workflow, pending_changes: null };
    },
    onSuccess: (edit) => settle(queryClient, workspaceId, edit, owner),
  });
}
